'use client'

import { mapCartItemToElevarAddToCartData, mapCartItemToElevarRemoveFromCartData } from '@repo/elevar/mapper'
import { CartItem } from '@repo/shopify/types'
import { Button } from '@repo/ui/components/button'
import { MinusIcon, PlusIcon } from 'lucide-react'
import { FunctionComponent, useActionState, useEffect } from 'react'
import { toast } from 'sonner'
import { updateCartItemQuantity } from '../../actions/cart.actions'
import { UpdateType } from '../../providers/CartProvider'
import { useTelemetry } from '../../providers/TelemetryProvider'
import { Dictionary } from '../../services/dictionary.service'
import { PageDto } from '../../types/page.types'

interface QuantityButtonProps {
  type: Exclude<UpdateType, 'delete'>
  dictionary: Dictionary
  disabled?: boolean
}

const QuantityButton: FunctionComponent<QuantityButtonProps> = ({ type, dictionary, disabled = false }) => {
  return (
    <Button
      type='submit'
      size='icon'
      variant='secondaryStatic'
      disabled={disabled}
      aria-label={type === 'plus' ? dictionary?.cart?.increaseitemquantity || 'Increase item quantity' : dictionary?.cart?.decreaseitemquantity || 'Decrease item quantity'}
    >
      {type === 'plus' && <PlusIcon />}
      {type === 'minus' && <MinusIcon />}
    </Button>
  )
}

interface Props {
  pageDto: PageDto
  item: CartItem
  type: Exclude<UpdateType, 'delete'>
  optimisticUpdateAction: (merchandiseId: string, updateType: UpdateType) => void
}

export const EditCartQuantity: FunctionComponent<Props> = ({ pageDto, item, type, optimisticUpdateAction }) => {
  const quantity = 1
  const { trackElevarAddToCartEvent, trackElevarRemoveFromCartEvent } = useTelemetry()
  const [state, formAction, pending] = useActionState(updateCartItemQuantity, null)

  const payload = {
    shopifyInstance: pageDto.shopifyInstance,
    merchandiseId: item.merchandise.id,
    quantity: type === 'plus' ? item.quantity + quantity : item.quantity - quantity,
  }

  const actionWithVariant = formAction.bind(null, payload)

  useEffect(() => {
    if (!pending && state?.status === 'error') {
      toast.error(state.message)
    }
  }, [state, pending, type])

  return (
    <form
      action={async () => {
        // A bit optimistic event dispatch here, but since this component could unmount (e.g. when qty becomes zero)
        // after completion, the state or pending variables might be null (depending on unmount timing), so we cannot
        // rely on it accurately.
        if (type === 'plus') {
          trackElevarAddToCartEvent(mapCartItemToElevarAddToCartData(item, pageDto.params.locale, quantity))
        } else {
          trackElevarRemoveFromCartEvent(mapCartItemToElevarRemoveFromCartData(item, pageDto.params.locale, quantity))
        }

        optimisticUpdateAction(payload.merchandiseId, type)
        actionWithVariant()
      }}
    >
      <QuantityButton type={type} disabled={pending} dictionary={pageDto.dictionary} />
    </form>
  )
}
